import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  Action,
  ClubVersion,
  Platform,
  UserRole
} from 'src/utils/datahubDefaultEvent/datahubDefaultEvent.enums'
import { useMemo } from 'react'
import DatahubDefaultEventUtils from 'src/utils/datahubDefaultEvent/datahubDefaultEvent'
import { getBrowserInfo } from 'src/utils/browserInfo'
import { useCommunityDetails } from 'src/pages/CommunityDetails/Posts/context/communityDetailsContext'
import { useSessionData } from '../useSessionData'
import { useMembershipData } from '../useMembershipData'
import {
  ClickEventPayload,
  ViewEventPayload,
  TryEventPayload,
  IBaseEvent,
  PathParams,
  EventWithEntity,
  OmitActionTrigger
} from './types'
import { EventBuilder } from './eventBuilder'

export const useTrackDatahubEvents = () => {
  const { community } = useCommunityDetails()
  const sessionData = useSessionData()

  const { membership: membershipSlug } = useParams() as PathParams
  const { data: membership } = useMembershipData({
    slug: membershipSlug
  })

  const baseEventData = useMemo<Omit<IBaseEvent, 'action'>>(() => {
    const { platformDetail, osVersion } = getBrowserInfo()

    return {
      platformDetail,
      osVersion,
      url: window.location.href,
      appVersion: process.env.PROJECT_VERSION || 'UNDEFINED',
      clubVersion: ClubVersion.MEMBERSHIP,
      platform: Platform.WEB,
      userId: sessionData?.user?.id || 0,
      ucode: sessionData?.user?.ucode || 'X',
      isLogged: Boolean(sessionData?.user?.isLogged),
      screenName: 'NOT_APP_EVENT',
      sessionId: 'X',
      userRole: UserRole.VIEWER,
      membershipId: membership?.id || sessionData?.membership?.id || 'UNDEFINED',
      membershipSlug: membership?.slug || sessionData?.membership?.slug || 'UNDEFINED'
    }
  }, [membership, sessionData])

  const eventBuilder = useMemo(() => new EventBuilder(baseEventData), [baseEventData])

  const sendDataHubEvent = useCallback(
    ({ action, entity, ...payload }: OmitActionTrigger<EventWithEntity>) => {
      DatahubDefaultEventUtils.sendEvent({ action, entity, event: payload })
    },
    []
  )

  const trackClickEvent = useCallback(
    <T>({
      payload,
      action
    }: {
      payload: OmitActionTrigger<ClickEventPayload> & T
      action: Action
    }) => {
      const event = eventBuilder.buildClickEvent({
        payload,
        action,
        community
      })
      sendDataHubEvent(event)
    },
    [eventBuilder, community, sendDataHubEvent]
  )

  const trackViewEvent = useCallback(
    <T>({
      payload,
      action
    }: {
      payload: OmitActionTrigger<ViewEventPayload> & T
      action: Action
    }) => {
      const event = eventBuilder.buildViewEvent({
        payload,
        action,
        community
      })
      sendDataHubEvent(event)
    },
    [eventBuilder, community, sendDataHubEvent]
  )

  const trackTryEvent = useCallback(
    <T>({
      payload,
      action
    }: {
      payload: OmitActionTrigger<TryEventPayload> & T
      action: Action
    }) => {
      const event = eventBuilder.buildTryEvent({
        payload,
        action,
        community
      })
      sendDataHubEvent(event)
    },
    [eventBuilder, community, sendDataHubEvent]
  )

  return {
    trackClickEvent,
    trackTryEvent,
    trackViewEvent
  }
}
